import {
  type EdgeData,
  type EdgeId,
  type NodeData,
  type NodeId,
  type NonZeroVector,
  type Point,
  getAngularDistance,
  getLoopbackEdgeEndAngle,
  isNonNullable,
  isTopologyNodeData,
  normalizeAngle,
  vector,
} from '@ipfabric/utils';
import { is } from '@ipfabric/utils/fp/comparison';
import { callMethod, exec } from '@ipfabric/utils/fp/function';
import { mapOfLists } from '@ipfabric/utils/fp/mapOfLists';
import { plainMap } from '@ipfabric/utils/fp/plainMap';
import { prop, setPropMutable } from '@ipfabric/utils/fp/prop';
import type {
  GraphRendererPlugin,
  GraphRendererPluginInterface,
} from 'components/Graph/GraphApp/plugins/GraphRendererPlugin';
import { constFalse, flow, pipe } from 'fp-ts/lib/function';
import {
  type DisplayObject,
  Ellipse,
  Graphics,
  type interaction,
} from 'pixi.js';

import { ManagedDomEventListeners } from '@ipfabric/ui-components/utils/dom/ManagedDomEventListeners';
import {
  type GraphEdgeDisplayObject,
  edgeObjectsFromIds,
  edgesDataFromIds,
  isLoopbackEdge,
} from 'components/Graph/GraphApp/GraphEdge';
import type { GraphNodeDisplayObject } from 'components/Graph/GraphApp/GraphNode';
import { createCurtain } from 'components/Graph/GraphApp/helpers/curtain';
import {
  ZINDEX_EDGE,
  ZINDEX_EDGE_HIGHLIGHTED,
  ZINDEX_EDGE_HIGHLIGHT_AREA,
  ZINDEX_HIGHLIGHT_CURTAIN,
  ZINDEX_HOVERED_EDGE_HIGHLIGHTED,
  ZINDEX_HOVERED_NODE_HIGHLIGHTED,
  ZINDEX_NODE,
  ZINDEX_NODE_HIGHLIGHTED,
} from 'components/Graph/GraphApp/zIndexes';
import { getEdgeOtherNodeId } from 'components/Graph/helpers/getEdgeOtherNodeId';
import { getNodeIconSize } from 'components/Graph/helpers/getNodeIconSize';
import { array, option } from 'fp-ts';
import type { Option } from 'fp-ts/lib/Option';
import type { Ord } from 'fp-ts/lib/Ord';
import type { Ordering } from 'fp-ts/lib/Ordering';
import { clearArray } from 'utils/array/clearArray';
import {
  ANIMATION_BACKWARD,
  type AnimationDirection,
  type BouncingAnimationController,
  createBouncingAnimationController,
} from 'utils/pixi/animation/BouncingAnimationController';
import { easeOutCubic } from 'utils/pixi/animation/easing';
import { suspendAnimateOnLowFps } from 'utils/pixi/animation/suspendAnimateOnLowFps';
import { spreadCloseAngles } from 'utils/spreadCloseAngles';

const HIGHLIGHT_DELAY = 500;
const HIGHLIGHT_ALPHA = 0.85;
const HIGHLIGHT_ANIMATION_TIME = 200;

const EDGE_HIGHLIGHT_AREA_SIZE = 55;
/**
 * Since it is difficult to exactly hover edges to different nodes that are too close,
 * edges closer to each other than this angle will have hover area shifted a bit
 * so that if you move mouse around a node, you should be able to highlight every edge at some angle.
 * See spreadCloseAngles function for details of the angle distribution algorithm.
 */
const DESIRED_MIN_ANGLE_DISTANCE = 0.05;

/**
 * Helper interface to group edges that have the same source-target pair
 *
 * They share the same "highlight" angle and will be highlighted at once on hover.
 */
interface EdgesOnAngle {
  angle: number;
  edges: GraphEdgeDisplayObject[];
}

interface NodeSubgraph {
  edgeHighlightActive: boolean;
  edgesByAngle: EdgesOnAngle[];
  nodeData: NodeData;
  relatedEdges: GraphEdgeDisplayObject[];
  relatedNodes: GraphNodeDisplayObject[];
}

function drawEdgeHighlightArea(nodeData: NodeData): Graphics {
  const hlArea = new Graphics();
  hlArea.position.set(nodeData.position.x, nodeData.position.y);
  hlArea.name = 'edge-highlight-area';
  hlArea.interactive = true;
  hlArea.zIndex = ZINDEX_EDGE_HIGHLIGHT_AREA;

  const nodeSize = getNodeIconSize(nodeData);
  const radiusX = nodeSize.width / 2 + EDGE_HIGHLIGHT_AREA_SIZE;
  const radiusY = nodeSize.height / 2 + EDGE_HIGHLIGHT_AREA_SIZE;
  const areaShape = new Ellipse(0, 0, radiusX, radiusY);

  hlArea.clear();
  hlArea.beginFill(0x999999, 0.4);
  hlArea.drawShape(areaShape);
  hlArea.hitArea = areaShape;
  hlArea.endFill();
  return hlArea;
}

const getMinHighlightAreaScale = (nodeData: NodeData): number => {
  const nodeSize = getNodeIconSize(nodeData);
  const shorterSizeHalf = Math.min(nodeSize.width, nodeSize.height) / 2;
  return shorterSizeHalf / (shorterSizeHalf + EDGE_HIGHLIGHT_AREA_SIZE);
};

type MouseArea = 'node' | 'edgeHighlightArea';

const edgesOnAnglesOrderByAngle: Ord<EdgesOnAngle> = {
  compare: (x, y) => Math.sign(x.angle - y.angle) as Ordering,
  equals: (x, y) => x.angle === y.angle,
};

const spreadCloseEdgesOnAngles = (
  edgesOnAngles: EdgesOnAngle[],
): EdgesOnAngle[] => {
  return pipe(
    edgesOnAngles,
    array.map(({ angle, ...rest }) => ({
      ...rest,
      angle: normalizeAngle(angle),
    })),
    array.sort(edgesOnAnglesOrderByAngle),
    spreadCloseAngles(
      DESIRED_MIN_ANGLE_DISTANCE,
      prop('angle'),
      (x, angle) => ({ ...x, angle }),
    ),
  );
};

const createHighlightCurtain = (
  pluginInterface: GraphRendererPluginInterface,
): [curtain: Graphics, drawCurtain: () => void] => {
  const [curtain, drawCurtain] = createCurtain(pluginInterface);
  curtain.name = 'highlight-related-curtain';
  curtain.zIndex = ZINDEX_HIGHLIGHT_CURTAIN;
  curtain.visible = false;
  pluginInterface.graphLayer.addChild(curtain);
  return [curtain, drawCurtain];
};

const getNodeData =
  (pluginInterface: GraphRendererPluginInterface) =>
  (nodeId: NodeId): Option<NodeData> =>
    option.fromNullable(pluginInterface.getGraphData().nodes[nodeId]);

const getRelatedNodeIds =
  (pluginInterface: GraphRendererPluginInterface) =>
  (nodeId: NodeId): NodeId[] => [
    nodeId,
    ...pipe(
      // Fixed noUncheckedIndexedAccess NIM-19408
      pluginInterface.getGraphData().nodesEdgesMap[nodeId]!,
      (set) => Array.from(set),
      edgesDataFromIds(pluginInterface.getGraphData().edges),
      array.map(getEdgeOtherNodeId(nodeId)),
    ),
  ];

const getRelatedEdgeIds =
  (pluginInterface: GraphRendererPluginInterface) =>
  (nodeId: NodeId): EdgeId[] =>
    // Fixed noUncheckedIndexedAccess NIM-19408
    Array.from(pluginInterface.getGraphData().nodesEdgesMap[nodeId]!.values());

const getRelatedEdgeDisplayObjects = (
  pluginInterface: GraphRendererPluginInterface,
  nodeId: NodeId,
): GraphEdgeDisplayObject[] =>
  pipe(
    nodeId,
    getRelatedEdgeIds(pluginInterface),
    edgeObjectsFromIds(pluginInterface.edgeDisplayObjects),
  );

const getRelatedNodes = (
  pluginInterface: GraphRendererPluginInterface,
  nodeId: NodeId,
): GraphNodeDisplayObject[] =>
  pipe(
    nodeId,
    getRelatedNodeIds(pluginInterface),
    array.map((id) => pluginInterface.nodeDisplayObjects.get(id)),
    array.filter(isNonNullable),
  );

const groupEdgesByOtherNode =
  (nodeId: NodeId) =>
  (edges: EdgeData[]): Map<NodeId, EdgeId[]> =>
    edges.reduce<Map<NodeId, EdgeId[]>>(
      (acc, edgeData) =>
        mapOfLists.pushMutable(
          getEdgeOtherNodeId(nodeId)(edgeData),
          edgeData.id,
        )(acc),
      new Map(),
    );

const getNonZeroVectorFromNodeToNode = (
  fromNode: NodeData,
): ((toNode: NodeData) => Option<NonZeroVector>) =>
  flow(
    prop('position'),
    (p) => vector.fromAtoB(fromNode.position, p),
    option.fromPredicate(vector.isNonZero),
  );

const edgesByOtherNodeToEdgesOnAngle = (
  pluginInterface: GraphRendererPluginInterface,
  thisNodeData: NodeData,
): ((edgesByOtherNode: Map<NodeId, EdgeId[]>) => EdgesOnAngle[]) =>
  plainMap.filterMapToArray(([otherNodeId, edgeIds]) =>
    pipe(
      otherNodeId,
      getNodeData(pluginInterface),
      option.chain(getNonZeroVectorFromNodeToNode(thisNodeData)),
      option.map(
        flow(vector.getAngle, (angle) => ({
          angle,
          edges: edgeObjectsFromIds(pluginInterface.edgeDisplayObjects)(
            edgeIds,
          ),
        })),
      ),
    ),
  );

const regularEdgesToEdgesOnAngle = (
  pluginInterface: GraphRendererPluginInterface,
  regularEdges: EdgeData[],
  nodeId: NodeId,
): EdgesOnAngle[] =>
  pipe(
    nodeId,
    getNodeData(pluginInterface),
    option.fold(
      () => [],
      (nodeData) =>
        pipe(
          regularEdges,
          groupEdgesByOtherNode(nodeId),
          edgesByOtherNodeToEdgesOnAngle(pluginInterface, nodeData),
        ),
    ),
  );

const loopbackEdgeToEdgesOnAngle =
  (pluginInterface: GraphRendererPluginInterface) =>
  (edge: EdgeData): EdgesOnAngle[] =>
    pipe(
      pluginInterface.edgeDisplayObjects,
      plainMap.get(edge.id),
      option.map((edgeObject) => {
        return [
          {
            angle: edge.shift,
            edges: [edgeObject],
          },
          {
            angle: getLoopbackEdgeEndAngle(edge.shift),
            edges: [edgeObject],
          },
        ];
      }),
      option.getOrElse<EdgesOnAngle[]>(() => []),
    );

/**
 * Activate edge highlight only when it "does something" ~ there are
 * at least 2 different elements to highlight on different angles.
 * Although it seems trivial, we have to compute it this way because:
 * - Regular edges are grouped by angle, so we have to count groups by angle.
 * - One loopback edge touches the node on 2 different angles, so we have
 *   to count the edges rather than counting them the same way with regular edges.
 */
const isEdgeHighlightActive = (
  regularEdgesOnAngles: EdgesOnAngle[],
  loopbackEdges: EdgeData[],
): boolean => regularEdgesOnAngles.length + loopbackEdges.length >= 2;

const isEdgeVisible = (
  pluginInterface: GraphRendererPluginInterface,
): ((edgeId: EdgeId) => boolean) =>
  flow(
    plainMap.getFrom(pluginInterface.edgeDisplayObjects),
    option.fold(constFalse, prop('visible')),
  );

const getEdgesByAngle = (
  pluginInterface: GraphRendererPluginInterface,
  nodeId: NodeId,
): {
  edgesByAngle: EdgesOnAngle[];
  edgeHighlightActive: boolean;
} => {
  const relatedEdgeIds = getRelatedEdgeIds(pluginInterface)(nodeId);

  const nodeData = pluginInterface.getGraphData().nodes[nodeId];
  if (!nodeData) {
    return { edgesByAngle: [], edgeHighlightActive: false };
  }

  const { left: regularEdges, right: loopbackEdges } = pipe(
    relatedEdgeIds,
    array.filter(isEdgeVisible(pluginInterface)),
    edgesDataFromIds(pluginInterface.getGraphData().edges),
    array.partition(isLoopbackEdge),
  );

  const regularEdgesOnAngles = regularEdgesToEdgesOnAngle(
    pluginInterface,
    regularEdges,
    nodeId,
  );
  const loopbackEdgesOnAngles = pipe(
    loopbackEdges,
    array.chain(loopbackEdgeToEdgesOnAngle(pluginInterface)),
  );

  return {
    edgeHighlightActive: isEdgeHighlightActive(
      regularEdgesOnAngles,
      loopbackEdges,
    ),
    edgesByAngle: pipe(
      [...regularEdgesOnAngles, ...loopbackEdgesOnAngles],
      spreadCloseEdgesOnAngles,
    ),
  };
};

const getNodeSubgraph =
  (pluginInterface: GraphRendererPluginInterface) =>
  (nodeData: NodeData): NodeSubgraph => ({
    nodeData,
    relatedEdges: getRelatedEdgeDisplayObjects(pluginInterface, nodeData.id),
    relatedNodes: getRelatedNodes(pluginInterface, nodeData.id),
    ...getEdgesByAngle(pluginInterface, nodeData.id),
  });

const initMousePressStateObserver = (
  pluginInterface: GraphRendererPluginInterface,
  onMouseDown: () => void,
  onMouseUp: () => void,
) => {
  const mousePressStateListeners = new ManagedDomEventListeners(
    [window, 'mousedown', onMouseDown],
    [window, 'mouseup', onMouseUp],
  );

  pluginInterface.events.addListener('beforeUnmount', () => {
    mousePressStateListeners.remove();
  });
};

const updateZIndex = (zIndex: number) =>
  setPropMutable<DisplayObject, 'zIndex'>('zIndex', zIndex);

const moveObjectsToFront = (objects: NodeSubgraph) => {
  objects.relatedNodes.forEach(updateZIndex(ZINDEX_NODE_HIGHLIGHTED));
  objects.relatedEdges.forEach(updateZIndex(ZINDEX_EDGE_HIGHLIGHTED));
  return objects;
};

const moveObjectsBack = (objects: NodeSubgraph) => {
  objects.relatedNodes.forEach(updateZIndex(ZINDEX_NODE));
  objects.relatedEdges.forEach(updateZIndex(ZINDEX_EDGE));
  return objects;
};

/**
 * Get scale of highlight area to fit it to node size
 *
 * Edge highlight area has a shrink/expand animation when it appears and disappears.
 * This
 * @param highlightedSubgraph
 */
const getFadedOutEdgeHighlightAreaScale = (
  highlightedSubgraph: Option<NodeSubgraph>,
): number =>
  pipe(
    highlightedSubgraph,
    option.fold(() => 1, flow(prop('nodeData'), getMinHighlightAreaScale)),
  );

const applyToRelatedEdges = (
  subgraph: Option<NodeSubgraph>,
  fn: (edges: GraphEdgeDisplayObject[]) => void,
) => {
  pipe(subgraph, option.map(flow(prop('relatedEdges'), fn)));
};

const getNonZeroVectorFromNodeToPoint =
  (p: Point) =>
  (nodeData: NodeData): Option<NonZeroVector> =>
    pipe(
      nodeData,
      prop('position'),
      (start) => vector.fromAtoB(start, p),
      option.fromPredicate(vector.isNonZero),
    );

const findClosestEdgesByAngle =
  (subgraph: NodeSubgraph) =>
  (angle: number): GraphEdgeDisplayObject[] =>
    pipe(
      subgraph,
      prop('edgesByAngle'),
      option.fromPredicate(array.isNonEmpty),
      option.map((a) =>
        a.reduce((closest, current) =>
          getAngularDistance(current.angle, angle) <
          getAngularDistance(closest.angle, angle)
            ? current
            : closest,
        ),
      ),
      option.fold(() => [], prop('edges')),
    );

const highlightEdge = (edge: GraphEdgeDisplayObject) => {
  edge.zIndex = ZINDEX_HOVERED_EDGE_HIGHLIGHTED;
};

const unhighlightEdge = (edge: GraphEdgeDisplayObject) => {
  edge.zIndex = ZINDEX_EDGE;
};

const moveEdgesClosestToVectorToFront = (
  highlightedSubgraph: NodeSubgraph,
): ((v: NonZeroVector) => void) =>
  flow(
    vector.getAngle,
    findClosestEdgesByAngle(highlightedSubgraph),
    array.map(highlightEdge),
  );

const moveClosestEdgesToFront =
  (p: Point) =>
  (subgraph: NodeSubgraph): void => {
    pipe(
      subgraph.nodeData,
      getNonZeroVectorFromNodeToPoint(p),
      option.map(moveEdgesClosestToVectorToFront(subgraph)),
    );
  };

const highlightEdgesAt = (subgraph: Option<NodeSubgraph>, p: Point): void => {
  applyToRelatedEdges(subgraph, array.map(unhighlightEdge));
  pipe(subgraph, option.map(moveClosestEdgesToFront(p)));
};

const finishExistingRunningAnimation: (
  controller: BouncingAnimationController,
) => void = flow(
  callMethod('getRunningAnimation'),
  option.map((ra) => ra.forceFinish()),
);

/**
 * Highlight subgraph when mouse hovers over a node
 *
 * Features:
 * - edges and nodes neighbouring with hovered node are highlighted
 * - if there are >=2 neighbour nodes, hover area is rendered around the node; moving the mouse
 *   in it highlights edges leading to a single other node
 *
 * Implementation notes:
 * This plugin is quite complicated because it covers two (tightly related) features above
 * and uses animations.
 * The plugin contains several frequently updated instance variables with a number
 * of inner functions bound to these variables. As much functions as possible are placed
 * at module-global level to reduce coupling. This is the best we could do to keep it
 * both readable and effective.
 * Main entry point is the `initializeHighlightForNode` method, that initializes this plugin
 * for each node and binds event handlers. This function contains some per-node variables
 * and bound functions.
 *
 * This plugin doesn't use `highlightGraphPart` partially on purpose, since the highlight effect
 * is extended with edge highlight area around the node, adding considerable complexity
 * to the animation and z-indexes. One day we may try to refactor this to be able
 * to work with `highlightGraphPart`, but it seems quite laborious and not cost-worthy
 * at the moment.
 */
export const highlightRelated: GraphRendererPlugin = (pluginInterface) => {
  /**
   * Collection of cleanup callbacks that will be triggered when highlight effect ends
   */
  const onHighlightExit: (() => void)[] = [];
  /**
   * Information about which nodes and edges are currently being highlighted,
   * or none if highlight effect is not active
   */
  let highlightedSubgraph: Option<NodeSubgraph> = option.none;
  /**
   * Area rendered under a node where single edges are highlighted on hover
   */
  let edgeHighlightArea: Graphics | null = null;
  /**
   * Keep information if primary mouse button is currently held down
   * (e.g. highlight effect is not triggered during drag)
   */
  let mouseDown = false;
  /**
   * Curtain element to fade all nodes and edges that aren't highlighted, in case
   * that something is highlighted
   */
  const [curtain, drawCurtain] = createHighlightCurtain(pluginInterface);

  /**
   * Given node id corresponds to the main node of currently highlighted subgraph
   */
  const isHighlighted = (nodeId: NodeId): boolean =>
    pipe(
      highlightedSubgraph,
      option.map(prop('nodeData', 'id')),
      option.exists(is(nodeId)),
    );

  const destroyEdgeHighlightArea = () => {
    edgeHighlightArea?.destroy();
    edgeHighlightArea = null;
  };

  /**
   * Remove highlight effect on current subgraph if present,
   * perform a cleanup of all possible initialized stuff
   * and cancel an associated animation that may be currently running
   */
  const destroyHighlight = (stopAnimation = true) => {
    option.map(moveObjectsBack)(highlightedSubgraph);
    curtain.visible = false;
    highlightedSubgraph = option.none;
    destroyEdgeHighlightArea();
    onHighlightExit.forEach(exec);
    clearArray(onHighlightExit);
    if (stopAnimation) {
      pipe(
        animationController.getRunningAnimation(),
        option.map(callMethod('cancel')),
      );
    }
  };

  /**
   * Render highlight fade in/out animation frame
   */
  const renderAnimationFrame = (
    phase: number,
    direction: AnimationDirection,
  ) => {
    if (direction === ANIMATION_BACKWARD && phase === 0) {
      destroyHighlight(false);
    }
    const minScale = getFadedOutEdgeHighlightAreaScale(highlightedSubgraph);
    curtain.alpha = phase * HIGHLIGHT_ALPHA;
    const hlAreaScale = minScale + phase * (1 - minScale);
    edgeHighlightArea?.scale.set(hlAreaScale, hlAreaScale);
  };

  /**
   * Initialize this plugin for each node added to the graph
   *
   * This is actually an "entry point" of this plugin.
   * This function sets up all handlers, instance variables etc. on a node
   * to prepare highlight effect on hover. It does not start the animation or so.
   */
  const initializeHighlightForNode = (
    node: GraphNodeDisplayObject,
    nodeId: NodeId,
  ) => {
    let timeout: number | undefined;
    let mouseArea: MouseArea | null = null;

    // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: reasonable readability
    const handleMouseOver = (ma: MouseArea) => {
      const nodeData = pluginInterface.getGraphData().nodes[nodeId];
      if (nodeData === undefined || !isTopologyNodeData(nodeData)) {
        return;
      }
      // no previous timeout should be active since the mouseout removes it,
      // but just to be sure no weird things happen
      window.clearTimeout(timeout);

      if (ma === 'node' && mouseArea === 'edgeHighlightArea') {
        stopEdgeHighlight();
      }

      mouseArea = ma;

      if (!mouseDown) {
        if (isHighlighted(nodeId)) {
          animationController.forward();
        } else {
          startDelayedHighlight();
        }
      }
    };

    const handleMouseOut = () => {
      window.clearTimeout(timeout);
      mouseArea = null;
      // when the mouse leaves the node when held (happens when dragging), do not animate
      if (!mouseDown) {
        animationController.backward();
      }
      stopEdgeHighlight();
    };

    const handleEdgeHighlightAreaMouseMove = (
      e: interaction.InteractionEvent,
    ) => {
      highlightEdgesAt(
        highlightedSubgraph,
        pluginInterface.viewport.toWorld(e.data.global),
      );
      pluginInterface.requestRender();
    };

    const handleOtherMouseMove = () => {
      if (mouseArea !== null) {
        handleMouseOut();
      }
    };

    /**
     * We need to check for mouse position because when mouse is on edge element
     * inside highlight area (so that edge appears as mouse move target element)
     * we still consider the mouse didn't leave edge highlight area.
     */
    const isInEdgeHighlightArea = (e: interaction.InteractionEvent) =>
      e.target === edgeHighlightArea ||
      edgeHighlightArea?.containsPoint(e.data.global);

    const handleMouseMove = (e: interaction.InteractionEvent) => {
      if (e.target === node) {
        // handleMouseOver('node') already fired by node.on('mouseover') below,
        // making this branch a no-op
      } else if (isInEdgeHighlightArea(e)) {
        handleEdgeHighlightAreaMouseMove(e);
      } else {
        handleOtherMouseMove();
      }
    };

    /**
     * When mouse leaves edge highlight area, show all related edges highlighted
     * rather than just the ones closest to mouse position
     */
    const stopEdgeHighlight = (): void => {
      applyToRelatedEdges(
        highlightedSubgraph,
        array.map(updateZIndex(ZINDEX_EDGE_HIGHLIGHTED)),
      );
      pluginInterface.requestRender();
    };

    const initEdgeHighlightArea = () => {
      pipe(
        nodeId,
        getNodeData(pluginInterface),
        option.map((nodeData) => {
          edgeHighlightArea = drawEdgeHighlightArea(nodeData);
          pluginInterface.graphLayer.addChild(edgeHighlightArea);
        }),
      );
    };

    /**
     * Start the actual highligh effect faded in by the animation,
     * setting up everything needed for it to work
     */
    const highlightThisNode = () => {
      if (isHighlighted(nodeId)) {
        return;
      }
      if (option.isSome(highlightedSubgraph)) {
        // some other node was highlighted
        destroyHighlight();
      }
      highlightedSubgraph = pipe(
        nodeId,
        getNodeData(pluginInterface),
        option.map(
          flow(getNodeSubgraph(pluginInterface), (subgraph) => {
            option.map(moveObjectsBack)(highlightedSubgraph);
            moveObjectsToFront(subgraph);
            updateZIndex(ZINDEX_HOVERED_NODE_HIGHLIGHTED)(node);

            if (subgraph.edgeHighlightActive) {
              initEdgeHighlightArea();
            }

            pluginInterface.viewport.on('mousemove', handleMouseMove);
            onHighlightExit.push(() => {
              pluginInterface.viewport.off('mousemove', handleMouseMove);
            });

            curtain.visible = true;
            drawCurtain();
            return subgraph;
          }),
        ),
      );
    };

    /**
     * Called when mouse hovered over a node to start the highlight effect
     * if the mouse doesn't leave the node for a given delay interval
     */
    const startDelayedHighlight = () => {
      timeout = window.setTimeout(() => {
        if (!mouseDown && !pluginInterface.isSelectionMode()) {
          highlightThisNode();
          animationController.forward();
        }
      }, HIGHLIGHT_DELAY);
    };

    node.on('mouseover', () => handleMouseOver('node'));
    node.on('mouseout', () => {
      // Fallback handler is used only during the timeout between hovering
      // the node and starting the highlight to kill the timeout.
      // Then, mouseout is triggered from handleMouseMove due to more complex logic
      // involving edge highlight area.
      if (!isHighlighted(nodeId)) {
        window.clearTimeout(timeout);
      }
    });

    node.on('pointerdown', () => {
      // prevent animation during drag etc
      window.clearTimeout(timeout);
      finishExistingRunningAnimation(animationController);
      animationController.backward();
    });
  };

  initMousePressStateObserver(
    pluginInterface,
    () => {
      mouseDown = true;
    },
    () => {
      mouseDown = false;
    },
  );

  // setup highlight animation effect as a two-way ("bouncing") animation that is automatically disabled
  // when low performance is detected
  const suspendableAnimate = suspendAnimateOnLowFps(
    pluginInterface.animate,
    pluginInterface.requestRender,
  );
  const animationController = createBouncingAnimationController(
    suspendableAnimate,
    HIGHLIGHT_ANIMATION_TIME,
    renderAnimationFrame,
    easeOutCubic,
  );

  // cancel any highligh effect when highlighted node is dragged
  pluginInterface.events.addListener('nodeDragStart', (e) => {
    if (e.nodeIds.some(isHighlighted)) {
      // destroy highlight area immediately since it looks weird when it shrinks slowly when dragging
      destroyEdgeHighlightArea();
      animationController.backward();
    }
  });

  // finally, set up handler to initialize the effect for each node added to the graph
  pluginInterface.events.addListener('nodeAdded', ({ node, id }) => {
    initializeHighlightForNode(node, id);
  });
};

export const testable = {
  HIGHLIGHT_ANIMATION_TIME,
  HIGHLIGHT_DELAY,
};
